import React, { Component, RefObject } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import Modal from 'react-modal';
import moment from 'moment';
import { SERVER_URL } from '../../../config';
import { AddInductionSession } from './AddInductionSession';
import CopyInductionSession from './CopyInductionSession';
import { TInductionSession, TPageNotification } from '../../../types';


type TInductionSessionCalendarProps = {
    scheduledEvents: any;
    currentDate: Date;
    afterDate: string;
    beforeDate: string;
    notification: TPageNotification;
    updateEvent: (eventInfo: any, revertFunc: () => void) => void;
    reloadData: () => void;
    datesRange: (dateInfo:{ start: Date; end: Date }) => void;
}

type TInductionSessionCalendarState = {
    showAddSessionModal: boolean;
    showCopySessionModal: boolean;
    scheduledEventsList: TInductionSession[];
    defaultDateTime: string;
}

export class InductionSessionCalendar extends Component <
    TInductionSessionCalendarProps,
    TInductionSessionCalendarState
> {

    state = {
        showAddSessionModal: false,
        showCopySessionModal: false,
        scheduledEventsList: this.props.scheduledEvents,
        defaultDateTime: '',
    }

    calendarRef: RefObject<FullCalendar> = React.createRef()

    handleEventClick = (eventInfo: { event: { id: any; start: Date}; }) => {
        const eventId = eventInfo.event.id;
        window.location.href =
            `${SERVER_URL}/dashboard/admin/induction-session-calendar/${eventId}`;
    };

    handleCreateNewEvent = (selectionInfo: { startStr: string; start: string | Date; }) => {
        this.setState({ showAddSessionModal: true, defaultDateTime: selectionInfo.startStr });
    }

    renderEventContent = (eventInfo: { event: { title: string; start: string; end: string}; }) => {
        const eventTitle = eventInfo.event.title;
        const eventStart = moment(eventInfo.event.start.toString()).format('hh:mm');
        const eventEnd = moment(eventInfo.event.end.toString()).format('hh:mm');

        let iconName: string;

        if (eventTitle.includes('Construction')) {
            iconName = 'construction-general';
        } else if (eventTitle.includes('Driving')) {
            iconName = 'box-time';
        } else if (eventTitle.includes('Hospitality')) {
            iconName = 'food-dome-serving-1';
        } else if (eventTitle.includes('Industrial')) {
            iconName = 'industry-general';
        } else if (eventTitle.includes('Office')) {
            iconName = 'office-general';
        } else if (eventTitle.includes('All sectors')) {
            iconName = 'box-time';
        } else {
            iconName = 'ellipsis';
        }
        return (
            <>
                <i className={`icon icon-${iconName} normal`} />
                <span className="event-label">[{eventStart}-{eventEnd}]{eventInfo.event.title}</span>
            </>
        );
    }

    getCustomButtons = () => {
        const currentViewType = this.calendarRef.current?.getApi().view.type;

        switch (currentViewType) {
            case 'timeGridDay':
                return ',copyDay';
            case 'timeGridWeek':
            case 'listWeek':
                return ',copyWeek';
            default:
                return '';
        }
    }

    handleOpenCopySessionModal = () =>
        this.setState({ showCopySessionModal: true })

    handleCloseCopySessionModal = () =>
        this.setState({ showCopySessionModal: false })

    render() {
        const { defaultDateTime } = this.state;

        return (
            <div className="table-container">
                <div>
                    <FullCalendar
                        plugins={[ dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin ]}
                        editable={true}
                        selectable={true}
                        displayEventEnd={true}
                        displayEventTime={true}
                        weekends={true}
                        eventDurationEditable={false}
                        height={850}
                        firstDay={1}
                        dragRevertDuration={1}
                        nowIndicator={true}
                        slotDuration="00:15:00"
                        slotMinTime="00:00:00"
                        slotMaxTime="24:00:00"
                        timeZone="local"
                        noEventsText="No Induction sessions scheduled"
                        headerToolbar={{
                            start: 'prev,next today',
                            center: 'title',
                            left: `dayGridMonth,timeGridWeek,timeGridDay,listWeek${this.getCustomButtons()}`
                        }}
                        customButtons={{
                            copyDay: {
                                text: 'copy day',
                                click: this.handleOpenCopySessionModal,
                            },
                            copyWeek: {
                                text: 'copy to next week',
                                click: this.handleOpenCopySessionModal,
                            },
                        }}
                        initialView="timeGridWeek"
                        allDaySlot={false}
                        events={this.props.scheduledEvents}
                        eventTimeFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            meridiem: false,
                            hour12: true
                        }}
                        eventClick={this.handleEventClick}
                        select={this.handleCreateNewEvent}
                        // @ts-ignore
                        eventDrop={this.props.updateEvent}
                        scrollTime={this.props.currentDate.toTimeString()}
                        datesSet={this.props.datesRange}
                        ref={this.calendarRef}
                        eventContent={this.renderEventContent}
                        dayHeaderContent={args => moment(args.date).format('ddd DD/MM')}
                        navLinks={true}
                    />
                    <Modal
                        className="none"
                        contentLabel="Add Induction Session Modal"
                        isOpen={this.state.showAddSessionModal}
                    >
                        <AddInductionSession
                            notification={this.props.notification}
                            closeModal={() => this.setState({ showAddSessionModal: false })}
                            defaultDateTime={defaultDateTime}
                            currentDate={this.props.currentDate}
                            scheduledEventsList={this.state.scheduledEventsList}
                            reloadData={this.props.reloadData}
                        />
                    </Modal>
                    <Modal
                        className="copy-session-modal"
                        contentLabel="Copy Induction Sessions"
                        isOpen={this.state.showCopySessionModal}
                        onRequestClose={this.handleCloseCopySessionModal}
                        appElement={document.getElementById('#react-root')}
                        >
                        <CopyInductionSession
                            notification={this.props.notification}
                            closeModal={this.handleCloseCopySessionModal}
                            afterDate={this.props.afterDate}
                            beforeDate={this.props.beforeDate}
                            calendarRef={this.calendarRef}
                        />
                    </Modal>
                </div>
            </div>

    );
  }
}
